import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import client from "../api/client";
import logo from "../components/AllVon/VonHeader/assets/logo.png";

export default function VerifyCertificate() {
  const { uuid } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    client
      .get(`/certificates/${uuid}/verify`)
      .then((res) => setState({ loading: false, data: res.data.data, error: null }))
      .catch((err) => {
        const msg =
          err.response?.data?.message || "Certificate not found or invalid link.";
        setState({ loading: false, data: null, error: msg });
      });
  }, [uuid]);

  return (
    <div style={styles.page}>
      {/* Top bar */}
      <div style={styles.topbar}>
        <div style={styles.topbarLogo}>
          <img src={logo} alt="E1X Academy" style={{ height: 26, display: "block" }} />
        </div>
        <span style={styles.topbarText}>...transitioning to tech with ease</span>
      </div>

      <div style={styles.wrapper}>
        {state.loading && (
          <div style={styles.loadingCard}>
            <div style={styles.spinner} />
            <p style={{ marginTop: 16, color: "#888", fontSize: 14 }}>Verifying certificate…</p>
          </div>
        )}

        {!state.loading && state.error && (
          <div style={styles.card}>
            <div style={{ ...styles.cardHeader, background: "#dc2626" }}>
              <div style={styles.iconCircle}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </div>
              <div>
                <h1 style={styles.cardHeaderTitle}>Verification Failed</h1>
                <p style={styles.cardHeaderSub}>{state.error}</p>
              </div>
            </div>
            <div style={styles.cardBody}>
              <p style={{ fontSize: 14, color: "#555" }}>
                This certificate could not be verified. It may have been revoked or the link may be incorrect.
              </p>
            </div>
          </div>
        )}

        {!state.loading && state.data && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.iconCircle}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div>
                <h1 style={styles.cardHeaderTitle}>Certificate Verified</h1>
                <p style={styles.cardHeaderSub}>This certificate is authentic and was issued by E1X Academy</p>
              </div>
            </div>

            <div style={styles.cardBody}>
              <div style={styles.certBadge}>Certificate of Completion</div>
              <div style={styles.studentName}>{state.data.student_name}</div>
              <div style={styles.certTagline}>Has successfully completed the following course</div>

              <table style={styles.table}>
                <tbody>
                  <DetailRow label="Course" value={state.data.course_name} />
                  {state.data.cohort && <DetailRow label="Cohort" value={state.data.cohort} />}
                  <DetailRow label="Issue Date" value={state.data.issue_date} />
                  <DetailRow label="Issued By" value={state.data.issued_by} />
                  <DetailRow
                    label="Certificate No."
                    value={<span style={styles.certNumBadge}>{state.data.certificate_number}</span>}
                  />
                </tbody>
              </table>
            </div>

            <div style={styles.cardFooter}>
              Verified on {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
              &nbsp;·&nbsp; ID: {state.data.uuid}
            </div>
          </div>
        )}

        <div style={styles.poweredBy}>
          Powered by <strong style={{ color: "#d15424" }}>E1X Academy</strong>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
      <td style={styles.tdLabel}>{label}</td>
      <td style={styles.tdValue}>{value}</td>
    </tr>
  );
}

const styles = {
  page: {
    fontFamily: "Arial, Helvetica, sans-serif",
    background: "#f4f6f9",
    minHeight: "100vh",
    paddingTop: 80,
    paddingBottom: 40,
    paddingLeft: 16,
    paddingRight: 16,
    color: "#1a1a1a",
  },
  topbar: {
    position: "fixed",
    top: 0, left: 0, right: 0,
    background: "#fff",
    borderBottom: "3px solid #d15424",
    padding: "10px 32px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    zIndex: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  topbarLogo: {
    background: "#d15424",
    padding: "4px 8px",
    lineHeight: 1,
  },
  topbarText: { fontSize: 12, color: "#888", fontStyle: "italic" },
  wrapper: { maxWidth: 560, margin: "0 auto" },
  loadingCard: {
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
    padding: "48px 28px",
    textAlign: "center",
  },
  spinner: {
    width: 36,
    height: 36,
    border: "3px solid #f0f0f0",
    borderTopColor: "#d15424",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto",
  },
  card: {
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
    overflow: "hidden",
  },
  cardHeader: {
    background: "#16a34a",
    padding: "22px 28px",
    color: "#fff",
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
  },
  cardHeaderTitle: { fontSize: 19, fontWeight: 700, marginBottom: 3 },
  cardHeaderSub: { fontSize: 13, opacity: 0.85 },
  iconCircle: {
    width: 36, height: 36,
    background: "rgba(255,255,255,0.2)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 2,
  },
  cardBody: { padding: "26px 28px" },
  certBadge: {
    display: "inline-block",
    background: "#fff7ed",
    border: "1.5px solid #d15424",
    borderRadius: 4,
    padding: "3px 10px",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: "#d15424",
    marginBottom: 16,
  },
  studentName: { fontSize: 24, fontWeight: 700, color: "#1a1a1a", marginBottom: 4 },
  certTagline: { fontSize: 13, color: "#888", marginBottom: 20 },
  table: { width: "100%", borderCollapse: "collapse" },
  tdLabel: {
    width: 130,
    color: "#888",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    paddingTop: 13,
    paddingBottom: 10,
    verticalAlign: "top",
  },
  tdValue: { fontWeight: 600, color: "#1a1a1a", padding: "10px 0", verticalAlign: "top" },
  certNumBadge: {
    fontFamily: "monospace",
    background: "#f4f6f9",
    borderRadius: 4,
    padding: "3px 8px",
    fontSize: 13,
  },
  cardFooter: {
    background: "#f9fafb",
    borderTop: "1px solid #eee",
    padding: "14px 28px",
    fontSize: 11,
    color: "#aaa",
  },
  poweredBy: { marginTop: 18, textAlign: "center", fontSize: 12, color: "#bbb" },
};
